import { ProductPageTemplate } from "@/components/site/ProductPageTemplate";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    title: "Jacks & Couplers",
    metaTitle: "Trailer Jacks & Couplers in Elkhart, IN - 800-10,000 lb",
    metaDescription: "Trailer tongue jacks, A-frame couplers, and surge actuators in Elkhart, IN - 800 to 10,000 lb. Manual, electric, and drop-leg models stocked at our parts counter.",
    heroEyebrow: "Jacks · Couplers · Actuators",
    heroHeading: "Front of the trailer, sorted.",
    heroLede: "Tongue jacks, A-frame couplers, surge brake actuators, and the accessories that go with them. From a manual sidewind to a 10,000 lb electric drop-leg, we have it.",
    intro: "The tongue is where everything starts: hooking up, brake actuation, leveling. We carry the components people actually buy - and we'll tell you the truth about what fails. (Spoiler: it's almost always cheap manual jacks. Just put a real one on it.)",
    features: [
      "Manual sidewind jacks - 1,000 to 5,000 lb",
      "Manual top-wind jacks - 2,000 to 7,000 lb",
      "Electric tongue jacks - 3,500 to 5,000 lb",
      "Drop-leg jacks for heavy applications",
      "A-frame couplers - 2 inch, 2-5/16 inch",
      "Straight-tongue and adjustable-channel couplers",
      "Surge brake actuators - drum and disc",
      "Safety chains, hooks, breakaway switches, and wiring"
    ],
    capacityTable: [
      { label: "Jack capacities", value: "800 – 10,000 lb" },
      { label: "Coupler sizes", value: "1-7/8\" · 2\" · 2-5/16\"" },
      { label: "Actuators", value: "Drum · Disc surge" },
      { label: "Brand", value: "Dexter · Bulldog · OEM" }
    ],
    variants: [
      { name: "Electric drop-leg jack", desc: "The upgrade most RV and camper owners eventually make. Faster setup, less strain, replaces manual sidewind." },
      { name: "Surge drum actuator", desc: "Common on boat trailers. Compresses on stopping to apply hydraulic drum brakes." },
      { name: "Surge disc actuator", desc: "Higher-pressure version for hydraulic disc brakes. The right call for heavy boat and equipment trailers." },
      { name: "Bolt-on A-frame coupler", desc: "When the existing coupler is rusted, cracked, or undersized - this is the swap." }
    ],
    related: [
      { title: "Hubs, Drums & Brakes", desc: "Pair surge actuators with the matching brake assembly." },
      { title: "Suspension Parts", desc: "Springs, hangers, equalizers." },
      { title: "Trailer Repair", desc: "Coupler swaps and surge brake service." }
    ]
  },
  es: {
    title: "Gatos y Acopladores",
    metaTitle: "Gatos y acopladores para remolque en Elkhart, IN - 800-10,000 lb",
    metaDescription: "Gatos de lengüeta, acopladores tipo A y actuadores de inercia para remolque en Elkhart, IN - 800 a 10,000 lb. Modelos manuales, eléctricos y de pata abatible disponibles en mostrador.",
    heroEyebrow: "Gatos · Acopladores · Actuadores",
    heroHeading: "La parte frontal de su remolque, resuelta.",
    heroLede: "Gatos de lengüeta, acopladores tipo A, actuadores de freno de inercia y los accesorios correspondientes. Desde un gato manual lateral hasta uno eléctrico de pata abatible de 10,000 lb, lo tenemos.",
    intro: "La lengüeta es donde todo comienza: el enganche, el accionamiento de frenos, la nivelación. Tenemos los componentes que la gente realmente compra, y le diremos la verdad sobre lo que falla. (Spoiler: casi siempre son los gatos manuales económicos. Instale uno de calidad).",
    features: [
      "Gatos manuales de montaje lateral - 1,000 a 5,000 lb",
      "Gatos manuales de montaje superior - 2,000 a 7,000 lb",
      "Gatos de lengüeta eléctricos - 3,500 a 5,000 lb",
      "Gatos de pata abatible para aplicaciones pesadas",
      "Acopladores tipo A - 2 pulgadas, 2-5/16 pulgadas",
      "Acopladores de lengüeta recta y de canal ajustable",
      "Actuadores de freno de inercia - tambor y disco",
      "Cadenas de seguridad, ganchos, interruptores de emergencia y cableado"
    ],
    capacityTable: [
      { label: "Capacidad de gatos", value: "800 – 10,000 lb" },
      { label: "Tamaños de acoplador", value: "1-7/8\" · 2\" · 2-5/16\"" },
      { label: "Actuadores", value: "Inercia tambor · disco" },
      { label: "Marca", value: "Dexter · Bulldog · OEM" }
    ],
    variants: [
      { name: "Gato eléctrico de pata abatible", desc: "La mejora que la mayoría de los propietarios de RV y remolques terminan haciendo. Instalación más rápida, menos esfuerzo, reemplaza al manual lateral." },
      { name: "Actuador de inercia para tambor", desc: "Común en remolques para botes. Se comprime al frenar para activar los frenos de tambor hidráulicos." },
      { name: "Actuador de inercia para disco", desc: "Versión de mayor presión para frenos de disco hidráulicos. La opción ideal para remolques pesados de botes y equipo." },
      { name: "Acoplador tipo A atornillable", desc: "Cuando el acoplador actual está oxidado, agrietado o es pequeño, este es el repuesto adecuado." }
    ],
    related: [
      { title: "Mazas, tambores y frenos", desc: "Combine actuadores de inercia con el conjunto de frenos adecuado." },
      { title: "Piezas de suspensión", desc: "Ballestas, soportes, ecualizadores." },
      { title: "Reparación de remolques", desc: "Cambio de acopladores y servicio de frenos de inercia." }
    ]
  }
};

export default function JacksCouplers() {
  const t = useCopy(COPY);

  return (
    <ProductPageTemplate
      slug="jacks-couplers"
      title={t.title}
      metaTitle={t.metaTitle}
      metaDescription={t.metaDescription}
      hero={{
        eyebrow: t.heroEyebrow,
        heading: t.heroHeading,
        lede: t.heroLede,
      }}
      intro={t.intro}
      features={t.features}
      capacityTable={t.capacityTable}
      variants={t.variants}
      related={[
        { title: t.related[0].title, path: "/products/hubs/", desc: t.related[0].desc },
        { title: t.related[1].title, path: "/products/suspension-parts/", desc: t.related[1].desc },
        { title: t.related[2].title, path: "/services/axle-repair/", desc: t.related[2].desc },
      ]}
    />
  );
}
